import { SearchMode } from "helpers/api";
import { useCallback, useEffect, useState } from "react";
import { getCookie, setCookie } from "react-use-cookie";
import { setSearchMode } from "store/slices/setting";
import { useAppDispatch, useAppSelector } from "./useStore";

const SEARCH_MODE_COOKIE = "searchMode";
// 30 days cookie expiration
const COOKIE_DAYS = 30;

/**
 * @hook useSearchMode
 * @description Encapsulates the business logic for managing search mode globally.
 */
const useSearchMode = () => {
  const dispatch = useAppDispatch();

  // The user's saved preference for search mode
  const [preferredSearchMode, setPreferredSearchMode] = useState<SearchMode>(
    () => {
      const savedMode = getCookie(SEARCH_MODE_COOKIE);
      return Object.values(SearchMode).includes(savedMode as SearchMode)
        ? (savedMode as SearchMode)
        : SearchMode.PRO_ANALYSIS; // Default to SUMMARY if not set
    }
  );

  const currentSearchMode = useAppSelector((state) => state.setting.searchMode);

  const handleSetSearchMode = useCallback(
    (mode: SearchMode) => {
      dispatch(setSearchMode(mode));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  // Initialize the search mode from stored preference
  useEffect(() => {
    // Always set from cookie preference on component mount
    handleSetSearchMode(preferredSearchMode);
  }, [handleSetSearchMode, preferredSearchMode]);

  // Handle changing the search mode
  const changeSearchMode = useCallback(
    async (mode: SearchMode) => {
      handleSetSearchMode(mode);

      // Save preference for next time with 30-day expiration
      setPreferredSearchMode(mode);
      setCookie(SEARCH_MODE_COOKIE, mode, {
        days: COOKIE_DAYS,
      });
    },
    [handleSetSearchMode]
  );

  return {
    currentSearchMode: currentSearchMode || SearchMode.PRO_ANALYSIS,
    preferredSearchMode,
    changeSearchMode,
    availableModes: Object.values(SearchMode),
  };
};

export default useSearchMode;
